// Messaging Pattern Detection Service
// Monitors user messaging behavior and detects suspicious patterns

export interface UserMessageStats {
  userId: string
  username: string
  role: string
  accountAge: number // days since account creation
  totalConversations: number
  vendorContacts: number
  messagesLast24h: number
  messagesLast7d: number
  uniqueVendorsContacted: number
  averageMessageLength: number
  spamScore: number
}

export interface SuspiciousPattern {
  id: string
  userId: string
  username: string
  patternType: 'vendor_spam' | 'message_flooding' | 'new_user_aggressive' | 'rapid_contact' | 'copy_paste'
  severity: 'low' | 'medium' | 'high' | 'critical'
  detectedAt: number
  description: string
  evidence: string[]
  autoAction?: 'rate_limit' | 'flag_for_review' | 'temporary_restrict'
  isResolved: boolean
  adminNotes?: string
}

export interface AlertThresholds {
  newUserVendorLimit: number // max vendors new users can contact per day
  rapidContactWindow: number // minutes
  rapidContactLimit: number // messages within window
  messageFloodLimit: number // messages per hour
  spamScoreThreshold: number // 0-100
  copyPasteThreshold: number // similarity percentage
}

class MessagingPatternDetectionService {
  private patterns: SuspiciousPattern[] = []
  private userStats: Map<string, UserMessageStats> = new Map()
  private messageHistory: Map<string, string[]> = new Map() // userId -> recent messages for copy/paste detection
  
  private thresholds: AlertThresholds = {
    newUserVendorLimit: 3, // New users can only contact 3 vendors per day
    rapidContactWindow: 5, // 5 minute window
    rapidContactLimit: 10, // 10 messages in 5 minutes triggers alert
    messageFloodLimit: 60, // 60 messages per hour
    spamScoreThreshold: 70, // Score above 70 is flagged
    copyPasteThreshold: 85 // 85% similarity triggers copy/paste detection
  }

  constructor() {
    this.loadFromStorage()
  }

  private loadFromStorage() {
    const patternsData = localStorage.getItem('suspicious_patterns')
    const userStatsData = localStorage.getItem('user_message_stats')

    if (patternsData) {
      this.patterns = JSON.parse(patternsData)
    }

    if (userStatsData) {
      const stats = JSON.parse(userStatsData)
      this.userStats = new Map(Object.entries(stats))
    }
  }

  private saveToStorage() {
    localStorage.setItem('suspicious_patterns', JSON.stringify(this.patterns))
    
    const statsObj = Object.fromEntries(this.userStats)
    localStorage.setItem('user_message_stats', JSON.stringify(statsObj))
  }

  // Analyze message and update user stats
  async analyzeMessage(senderId: string, receiverId: string, content: string, isVendorContact: boolean): Promise<void> {
    const now = Date.now()
    
    // Update user stats
    await this.updateUserStats(senderId, receiverId, content, isVendorContact)
    
    // Get current user stats
    const senderStats = this.userStats.get(senderId)
    if (!senderStats) return

    // Check for suspicious patterns
    await this.checkVendorSpamPattern(senderId, senderStats, isVendorContact)
    await this.checkMessageFloodingPattern(senderId, senderStats)
    await this.checkNewUserAggressivePattern(senderId, senderStats)
    await this.checkRapidContactPattern(senderId)
    await this.checkCopyPastePattern(senderId, content)

    this.saveToStorage()
  }

  private async updateUserStats(senderId: string, receiverId: string, content: string, isVendorContact: boolean): Promise<void> {
    const now = Date.now()
    const dayMs = 24 * 60 * 60 * 1000
    const weekMs = 7 * dayMs

    let stats = this.userStats.get(senderId)
    
    if (!stats) {
      // Get user info from localStorage
      const users = JSON.parse(localStorage.getItem('users') || '[]')
      const user = users.find((u: any) => u.id === senderId)
      
      stats = {
        userId: senderId,
        username: user?.username || 'Unknown',
        role: user?.role || 'member',
        accountAge: user?.dateCreated ? Math.floor((now - new Date(user.dateCreated).getTime()) / dayMs) : 0,
        totalConversations: 0,
        vendorContacts: 0,
        messagesLast24h: 0,
        messagesLast7d: 0,
        uniqueVendorsContacted: 0,
        averageMessageLength: 0,
        spamScore: 0
      }
    }

    // Update stats
    stats.totalConversations++
    if (isVendorContact) {
      stats.vendorContacts++
    }

    // Count recent messages (this is simplified - in real app would track timestamps)
    stats.messagesLast24h++
    stats.messagesLast7d++

    // Update average message length
    const currentAvg = stats.averageMessageLength
    const messageCount = stats.totalConversations
    stats.averageMessageLength = ((currentAvg * (messageCount - 1)) + content.length) / messageCount

    // Calculate spam score
    stats.spamScore = this.calculateSpamScore(stats)

    this.userStats.set(senderId, stats)

    // Store recent messages for copy/paste detection
    const userMessages = this.messageHistory.get(senderId) || []
    userMessages.push(content)
    // Keep only last 10 messages
    if (userMessages.length > 10) {
      userMessages.shift()
    }
    this.messageHistory.set(senderId, userMessages)
  }

  private calculateSpamScore(stats: UserMessageStats): number {
    let score = 0

    // New account factor
    if (stats.accountAge < 1) score += 20
    else if (stats.accountAge < 7) score += 10

    // High vendor contact ratio
    const vendorRatio = stats.vendorContacts / Math.max(stats.totalConversations, 1)
    if (vendorRatio > 0.8) score += 30
    else if (vendorRatio > 0.5) score += 15

    // Message frequency
    if (stats.messagesLast24h > 80) score += 25
    else if (stats.messagesLast24h > 60) score += 10

    // Very short messages (potential spam)
    if (stats.averageMessageLength < 10) score += 15

    return Math.min(score, 100)
  }

  private async checkVendorSpamPattern(userId: string, stats: UserMessageStats, isVendorContact: boolean): Promise<void> {
    if (!isVendorContact) return

    // Check if new user is contacting too many vendors
    if (stats.accountAge <= 7 && stats.vendorContacts > this.thresholds.newUserVendorLimit) {
      await this.createSuspiciousPattern({
        userId,
        username: stats.username,
        patternType: 'vendor_spam',
        severity: 'high',
        description: `New user contacted ${stats.vendorContacts} vendors (limit: ${this.thresholds.newUserVendorLimit})`,
        evidence: [
          `Account age: ${stats.accountAge} days`,
          `Vendor contacts: ${stats.vendorContacts}`,
          `Total conversations: ${stats.totalConversations}`
        ],
        autoAction: 'rate_limit'
      })
    }
  }

  private async checkMessageFloodingPattern(userId: string, stats: UserMessageStats): Promise<void> {
    if (stats.messagesLast24h > this.thresholds.messageFloodLimit) {
      await this.createSuspiciousPattern({
        userId,
        username: stats.username,
        patternType: 'message_flooding',
        severity: stats.messagesLast24h > 80 ? 'critical' : 'high',
        description: `User sent ${stats.messagesLast24h} messages in 24 hours`,
        evidence: [
          `Messages last 24h: ${stats.messagesLast24h}`,
          `Spam score: ${stats.spamScore}`,
          `Average message length: ${stats.averageMessageLength}`
        ],
        autoAction: stats.messagesLast24h > 80 ? 'temporary_restrict' : 'flag_for_review'
      })
    }
  }

  private async checkNewUserAggressivePattern(userId: string, stats: UserMessageStats): Promise<void> {
    if (stats.accountAge <= 3 && stats.spamScore > this.thresholds.spamScoreThreshold) {
      await this.createSuspiciousPattern({
        userId,
        username: stats.username,
        patternType: 'new_user_aggressive',
        severity: 'high',
        description: `New user showing aggressive messaging patterns`,
        evidence: [
          `Account age: ${stats.accountAge} days`,
          `Spam score: ${stats.spamScore}`,
          `Vendor contact ratio: ${Math.round((stats.vendorContacts / stats.totalConversations) * 100)}%`
        ],
        autoAction: 'flag_for_review'
      })
    }
  }

  private async checkRapidContactPattern(userId: string): Promise<void> {
    // This is simplified - in real app would track message timestamps
    const stats = this.userStats.get(userId)
    if (!stats) return

    // Simulate rapid contact detection based on recent activity
    if (stats.messagesLast24h > 0 && (stats.messagesLast24h / 24) > (this.thresholds.rapidContactLimit / (this.thresholds.rapidContactWindow / 60))) {
      await this.createSuspiciousPattern({
        userId,
        username: stats.username,
        patternType: 'rapid_contact',
        severity: 'medium',
        description: `User sending messages at rapid rate (10+ in 5 minutes)`,
        evidence: [
          `High message frequency detected`,
          `Messages last 24h: ${stats.messagesLast24h}`,
          `Account age: ${stats.accountAge} days`
        ],
        autoAction: 'rate_limit'
      })
    }
  }

  private async checkCopyPastePattern(userId: string, newMessage: string): Promise<void> {
    const userMessages = this.messageHistory.get(userId) || []
    if (userMessages.length < 2) return

    // Check similarity with recent messages
    for (const previousMessage of userMessages.slice(-5)) {
      const similarity = this.calculateMessageSimilarity(newMessage, previousMessage)
      
      if (similarity > this.thresholds.copyPasteThreshold) {
        const stats = this.userStats.get(userId)
        await this.createSuspiciousPattern({
          userId,
          username: stats?.username || 'Unknown',
          patternType: 'copy_paste',
          severity: 'medium',
          description: `User sending similar/copied messages`,
          evidence: [
            `Message similarity: ${similarity}%`,
            `Potential copy/paste behavior detected`,
            `Recent message pattern analysis`
          ],
          autoAction: 'flag_for_review'
        })
        break
      }
    }
  }

  private calculateMessageSimilarity(message1: string, message2: string): number {
    if (message1.length === 0 && message2.length === 0) return 100
    if (message1.length === 0 || message2.length === 0) return 0

    const longer = message1.length > message2.length ? message1 : message2
    const shorter = message1.length > message2.length ? message2 : message1
    
    if (longer.length === 0) return 100

    const distance = this.levenshteinDistance(longer, shorter)
    return ((longer.length - distance) / longer.length) * 100
  }

  private levenshteinDistance(str1: string, str2: string): number {
    const matrix = []

    for (let i = 0; i <= str2.length; i++) {
      matrix[i] = [i]
    }

    for (let j = 0; j <= str1.length; j++) {
      matrix[0][j] = j
    }

    for (let i = 1; i <= str2.length; i++) {
      for (let j = 1; j <= str1.length; j++) {
        if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1]
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          )
        }
      }
    }

    return matrix[str2.length][str1.length]
  }

  private async createSuspiciousPattern(pattern: Omit<SuspiciousPattern, 'id' | 'detectedAt' | 'isResolved'>): Promise<void> {
    // Check if similar pattern already exists for this user
    const existingPattern = this.patterns.find(p => 
      p.userId === pattern.userId && 
      p.patternType === pattern.patternType && 
      !p.isResolved &&
      (Date.now() - p.detectedAt) < 24 * 60 * 60 * 1000 // Within last 24 hours
    )

    if (existingPattern) {
      // Update existing pattern instead of creating duplicate
      existingPattern.evidence.push(...pattern.evidence)
      existingPattern.severity = pattern.severity
      return
    }

    const newPattern: SuspiciousPattern = {
      id: `pattern_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      detectedAt: Date.now(),
      isResolved: false,
      ...pattern
    }

    this.patterns.push(newPattern)
  }

  // Admin functions
  getSuspiciousPatterns(includeResolved = false): SuspiciousPattern[] {
    return this.patterns
      .filter(p => includeResolved || !p.isResolved)
      .sort((a, b) => b.detectedAt - a.detectedAt)
  }

  getUserStats(userId: string): UserMessageStats | undefined {
    return this.userStats.get(userId)
  }

  getAllUserStats(): UserMessageStats[] {
    return Array.from(this.userStats.values())
      .sort((a, b) => b.spamScore - a.spamScore)
  }

  resolvePattern(patternId: string, adminNotes: string): void {
    const pattern = this.patterns.find(p => p.id === patternId)
    if (pattern) {
      pattern.isResolved = true
      pattern.adminNotes = adminNotes
      this.saveToStorage()
    }
  }

  updateThresholds(newThresholds: Partial<AlertThresholds>): void {
    this.thresholds = { ...this.thresholds, ...newThresholds }
  }

  getThresholds(): AlertThresholds {
    return { ...this.thresholds }
  }

  // Check if user should be rate limited
  shouldRateLimit(userId: string): boolean {
    const recentPatterns = this.patterns.filter(p => 
      p.userId === userId && 
      !p.isResolved && 
      (p.autoAction === 'rate_limit' || p.autoAction === 'temporary_restrict') &&
      (Date.now() - p.detectedAt) < 60 * 60 * 1000 // Within last hour
    )

    return recentPatterns.length > 0
  }

  // Get rate limit info for user
  getRateLimitInfo(userId: string): { isLimited: boolean; reason?: string; expiresAt?: number } {
    const limitingPattern = this.patterns.find(p => 
      p.userId === userId && 
      !p.isResolved && 
      (p.autoAction === 'rate_limit' || p.autoAction === 'temporary_restrict') &&
      (Date.now() - p.detectedAt) < 60 * 60 * 1000
    )

    if (limitingPattern) {
      return {
        isLimited: true,
        reason: limitingPattern.description,
        expiresAt: limitingPattern.detectedAt + (60 * 60 * 1000) // 1 hour from detection
      }
    }

    return { isLimited: false }
  }

  // Clear old patterns (housekeeping)
  clearOldPatterns(daysOld = 30): void {
    const cutoff = Date.now() - (daysOld * 24 * 60 * 60 * 1000)
    this.patterns = this.patterns.filter(p => p.detectedAt > cutoff)
    this.saveToStorage()
  }
}

export const messagingPatternDetection = new MessagingPatternDetectionService()